import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import TevelCyberLogo from "../../../assets/img/TevelCyberFinalBlueLogo.png";
import LoginImage from "../../../assets/img/LoginIllustraion.png";
import QuestionsService from "../../../services/QuestionService";
import AlertService from "../../../services/AlertService";
import CyberBay from "../../../assets/img/CyberBayFinalBlueLogo.png";
import AnswerIcon from "../../../assets/icons/AnswerIcon.svg";
import LockIcon from "../../../assets/icons/lockIcon2.svg";

const Question = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadControllerRef = useRef(null);
  const submitControllerRef = useRef(null);
  const [validations, setValidations] = useState({
    q1: true,
    a1: true,
    q2: true,
    a2: true,
    q3: true,
    a3: true,
  });

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  // Refs for form inputs
  const q1Ref = useRef();
  const a1Ref = useRef();
  const q2Ref = useRef();
  const a2Ref = useRef();
  const q3Ref = useRef();
  const a3Ref = useRef();

const loadQuestions = useCallback(async () => {
  if (loadControllerRef.current) {
    loadControllerRef.current.abort();
  }
  const controller = new AbortController();
  loadControllerRef.current = controller;

  try {
    const data = await QuestionsService.GetAllQuestions(
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      setQuestions(data.data);
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Failed to load questions");
  }
}, [authContext]);

useEffect(() => {
  loadQuestions();
  return () => loadControllerRef.current?.abort();
}, [loadQuestions]);

// Unmount cleanup
useEffect(() => {
  return () => {
    loadControllerRef.current?.abort();
  };
}, []);

const handleSubmit = async (e) => {
  e.preventDefault();
  setValidations({
    q1: true, a1: true,
    q2: true, a2: true,
    q3: true, a3: true,
  });

  const inputs = {
    q1: q1Ref.current.value,
    a1: a1Ref.current.value,
    q2: q2Ref.current.value,
    a2: a2Ref.current.value,
    q3: q3Ref.current.value,
    a3: a3Ref.current.value,
  };

  const newValidations = { ...validations };
  let isValid = true;
  Object.entries(inputs).forEach(([key, value]) => {
    if (!value) {
      newValidations[key] = false;
      isValid = false;
    }
  });
  if (!isValid) return setValidations(newValidations);

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = [
      { questionId: inputs.q1, answer: inputs.a1 },
      { questionId: inputs.q2, answer: inputs.a2 },
      { questionId: inputs.q3, answer: inputs.a3 },
    ];

    const response = await QuestionsService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (response.isSuccess) {
      authContext.setMFASetupCompleted(true);
      authContext.setAnswered(true);
      navigate("/logout");
    } else {
      AlertService.error(response.errorMessage);
    }
  } catch (error) {
    if (error.name === "AbortError") return;
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  return (
    <div className="container-fluid min-vh-100 d-flex flex-column flex-xl-row p-0">
      <div className="col-xl-6 d-flex flex-column justify-content-center align-items-center p-3 p-lg-5 min-vh-100  ">
        <div className="d-none d-xl-block">
          <img
            src={TevelCyberLogo}
            alt="Company Logo"
            style={{ maxWidth: "217px", marginRight: "220px" }}
            className="img-fluid"
          />
        </div>
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100">
          <div className="d-flex d-xl-none justify-content-between align-items-center gap-5 mt-3 mb-4">
            <img
              src={TevelCyberLogo}
              alt="Tevel Cyber Logo"
              className="img-fluid"
              style={{ width: "140px" }}
            />
            <img
              src={CyberBay}
              alt="Cyber Bay Logo"
              className="img-fluid"
              style={{ width: "120px" }}
            />
          </div>
          <div className="w-100 mb-xl-4" style={{ maxWidth: "440px" }}>
            <h4 className="mb-3  text-center text-xl-start fw-bold">
              Security Questions
            </h4>
            <form onSubmit={handleSubmit}>
              {[1, 2, 3].map((num) => (
                <div className="mb-3" key={num}>
                  <div className="input-group mb-3">
                    <span className="input-group-text bg-transparent">
                      <img src={LockIcon} alt="lock" />
                    </span>
                    <select
                      ref={[q1Ref, q2Ref, q3Ref][num - 1]}
                      className={`form-select custom-input border-start-0`}
                      aria-label={`Security question ${num}`}
                    >
                      <option value="" disabled>
                        Select question {num}
                      </option>
                      {questions.map((question) => (
                        <option key={question.id} value={question.id}>
                          {question.question}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!validations[`q${num}`] && (
                    <div
                      className="text-danger mb-2"
                      style={{ fontSize: "0.875em" }}
                    >
                      Please select a question
                    </div>
                  )}

                  <div className="input-group">
                    <span className="input-group-text bg-transparent border-end-0">
                      <img
                        src={AnswerIcon}
                        alt="answer"
                        style={{ color: "#ccc" }}
                        className=" ps-1"
                      />
                    </span>
                    <input
                      type="text"
                      ref={[a1Ref, a2Ref, a3Ref][num - 1]}
                      className={`form-control custom-input border-start-0 `}
                      placeholder="Enter your answer"
                    />
                  </div>
                  {!validations[`a${num}`] && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "0.875em" }}
                    >
                      Please provide an answer
                    </div>
                  )}
                </div>
              ))}
              <div className="d-flex justify-content-between mt-4">
                <button
                  type="button"
                  className="btn text-decoration-none  btn-outline-login custom-input px-4"
                  onClick={() => navigate("/login")}
                  style={{ maxWidth: "180px" }}
                >
                  Back to Login
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-5 custom-input"
                  disabled={loading}
                >
                  {loading ? (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : null}
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
        <footer className="mt-auto text-center w-100">
          <p className="text-muted small mb-0">
            © 2026 Tevel Cyber Corps Pvt. Ltd. All rights reserved - Version 2.2.0
          </p>
        </footer>
      </div>
      <div className="col-xl-6 d-none d-xl-flex flex-column login-bg  min-vh-100 align-items-center justify-content-center  ">
        <div className="p-5 mt-2">
          <img
            src={CyberBay}
            alt="CYBER BAY"
            className="img-fluid mb-4"
            width="130px"
          />
        </div>
        <div className='"d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100 p-xl-5'>
          <img
            src={LoginImage}
            alt="Decorative"
            className="img-fluid loginImg"
            style={{
              maxWidth: "690px",
              maxHeight: "484px",
              marginLeft: "130px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Question;
